/**
 * The finance API, as the screens see it (P1-TR3).
 *
 * Every money field is a string on the way in and on the way out (MONEY-2).
 * Nothing in this file parses one into a number — the display formatter takes
 * strings and the server takes strings, so there is no point at which a float
 * could get in.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { api } from '../../api/client.js';
import { compressImage } from '../../lib/imageCompress.js';

export interface Transaction {
  id: number;
  txnDate: string;
  type: string;
  amount: string;
  status: 'pending' | 'approved' | 'rejected' | 'voided';
  categoryId: number | null;
  accountId: number | null;
  partnerId: number | null;
  vendorId: number | null;
  note: string | null;
  receiptUrl: string | null;
  createdById: number;
  approvedById: number | null;
  approvedAt: string | null;
  rejectReason: string | null;
  voidReason: string | null;
  createdAt: string;
  isBackdated: boolean;
  /** BR-08a — the admin changed this entry after approval (D-017). */
  amendReason: string | null;
  amendedById: number | null;
  amendedAt: string | null;
}

export interface Category {
  id: number;
  name: string;
  kind: string;
  parentId: number | null;
  isActive: boolean;
  children?: Category[];
}

export interface Account {
  id: number;
  name: string;
  type: string;
  isActive: boolean;
  balance: string;
}

/** §12.1 — one camera, as stored in settings. */
export interface CctvLink {
  name: string;
  url: string;
}

export interface Settings {
  receiptRequiredAbove: string;
  farmName: string;
  /**
   * `unknown` because it is a JSON column: the server validates the shape on
   * write, and the screen re-checks each URL before rendering an anchor rather
   * than trusting this type (SEC-4).
   */
  cctvLinks: unknown;
}

export interface TransactionFilters {
  status?: string;
  type?: string;
  categoryId?: number;
  accountId?: number;
  from?: string;
  to?: string;
  page?: number;
  limit?: number;
}

interface Envelope<T> {
  success: true;
  data: T;
  meta?: { page: number; limit: number; total: number };
}

export interface Page<T> {
  items: T[];
  total: number;
}

/** Query keys in one place, so an invalidation cannot miss a screen. */
export const financeKeys = {
  transactions: (filters: TransactionFilters = {}) => ['transactions', filters] as const,
  categories: () => ['categories'] as const,
  accounts: () => ['accounts'] as const,
  settings: () => ['settings'] as const,
  dashboard: () => ['dashboard', 'finance'] as const,
};

export function useCategories(): UseQueryResult<Category[]> {
  return useQuery({
    queryKey: financeKeys.categories(),
    queryFn: async () =>
      (await api.get<Envelope<Category[]>>('/categories', { params: { kind: 'expense' } })).data.data,
    // Reference data. Refetching the category tree on every visit to the
    // expense form is a request a partner pays for in data and in waiting.
    staleTime: 30 * 60 * 1000,
  });
}

export function useAccounts(): UseQueryResult<Account[]> {
  return useQuery({
    queryKey: financeKeys.accounts(),
    queryFn: async () => (await api.get<Envelope<Account[]>>('/accounts')).data.data,
  });
}

export function useSettings(): UseQueryResult<Settings> {
  return useQuery({
    queryKey: financeKeys.settings(),
    queryFn: async () => (await api.get<Envelope<Settings>>('/settings')).data.data,
    staleTime: 30 * 60 * 1000,
  });
}

export function useTransactions(filters: TransactionFilters = {}): UseQueryResult<Page<Transaction>> {
  return useQuery({
    queryKey: financeKeys.transactions(filters),
    queryFn: async () => {
      const response = await api.get<Envelope<Transaction[]>>('/transactions', { params: filters });
      return { items: response.data.data, total: response.data.meta?.total ?? response.data.data.length };
    },
  });
}

export interface NewExpense {
  txnDate: string;
  type: string;
  /** Already `123.45`; never a number (MONEY-1). */
  amount: string;
  categoryId?: number;
  accountId?: number;
  vendorId?: number;
  /** BR-03 — required for capital_in and drawing: whose money it is. */
  partnerId?: number;
  note?: string;
  receiptUrl?: string;
}

/**
 * Uploads a receipt photo, compressing it first (P1-TR4).
 *
 * Returns the id the transaction stores in `receiptUrl`. The upload happens
 * before the transaction is created, so a partner on a weak connection who
 * loses the second request has not lost the photo — retrying the submit reuses
 * the id rather than re-uploading five megabytes.
 */
export async function uploadReceipt(file: File): Promise<string> {
  const { file: compressed } = await compressImage(file);

  const form = new FormData();
  form.append('photo', compressed);

  const response = await api.post<Envelope<{ id: string }>>('/uploads', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    // A photo on 3G takes longer than the default 20s.
    timeout: 90_000,
  });

  return response.data.data.id;
}

/** Every screen that shows a number is stale the moment an entry changes. */
function useFinanceInvalidation(): () => Promise<void> {
  const queryClient = useQueryClient();

  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['transactions'] }),
      queryClient.invalidateQueries({ queryKey: financeKeys.accounts() }),
      queryClient.invalidateQueries({ queryKey: financeKeys.dashboard() }),
    ]);
  };
}

export function useCreateTransaction(): UseMutationResult<Transaction, unknown, NewExpense> {
  const invalidate = useFinanceInvalidation();

  return useMutation({
    mutationFn: async (input: NewExpense) => (await api.post<Envelope<Transaction>>('/transactions', input)).data.data,
    onSuccess: invalidate,
  });
}

export function useApproveTransaction(): UseMutationResult<Transaction, unknown, number> {
  const invalidate = useFinanceInvalidation();

  return useMutation({
    mutationFn: async (id: number) =>
      (await api.post<Envelope<Transaction>>(`/transactions/${String(id)}/approve`)).data.data,
    onSuccess: invalidate,
  });
}

export function useRejectTransaction(): UseMutationResult<Transaction, unknown, { id: number; reason: string }> {
  const invalidate = useFinanceInvalidation();

  return useMutation({
    mutationFn: async ({ id, reason }) =>
      (await api.post<Envelope<Transaction>>(`/transactions/${String(id)}/reject`, { reason })).data.data,
    onSuccess: invalidate,
  });
}

export interface LedgerEntry {
  id: number;
  txnDate: string;
  type: string;
  amount: string;
  /** Signed by what it did to the account: money in is positive. */
  effect: string;
  runningBalance: string;
  note: string | null;
  categoryId: number | null;
}

export interface Ledger {
  accountId: number;
  openingBalance: string;
  closingBalance: string;
  entries: LedgerEntry[];
  page: number;
  limit: number;
  total: number;
}

export interface Vendor {
  id: number;
  name: string;
  phone: string | null;
  isActive: boolean;
  payable: string;
}

export interface NewVendor {
  name: string;
  phone?: string;
  address?: string;
  note?: string;
}

export interface VendorChanges {
  name?: string;
  phone?: string | null;
  address?: string | null;
  note?: string | null;
  /** BR-08 — deactivate, never delete. */
  isActive?: boolean;
}

function useVendorInvalidation(): () => Promise<void> {
  const queryClient = useQueryClient();

  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['vendors'] }),
      // The dashboard carries the total payables figure (BR-10).
      queryClient.invalidateQueries({ queryKey: financeKeys.dashboard() }),
    ]);
  };
}

/**
 * Adds a shop or supplier (BR-10).
 *
 * The endpoint has existed since P1 and nothing called it, so the feed shop a
 * partner buys from on credit could not be created — and without a vendor
 * there is nothing for a credit purchase to be recorded against (P1-BR5).
 */
export function useCreateVendor(): UseMutationResult<Vendor, unknown, NewVendor> {
  const invalidate = useVendorInvalidation();

  return useMutation({
    mutationFn: async (input: NewVendor) => (await api.post<Envelope<Vendor>>('/vendors', input)).data.data,
    onSuccess: invalidate,
  });
}

export function useUpdateVendor(): UseMutationResult<Vendor, unknown, { id: number; changes: VendorChanges }> {
  const invalidate = useVendorInvalidation();

  return useMutation({
    mutationFn: async ({ id, changes }) =>
      (await api.patch<Envelope<Vendor>>(`/vendors/${String(id)}`, changes)).data.data,
    onSuccess: invalidate,
  });
}

export interface Asset {
  id: number;
  name: string;
  categoryId: number;
  categoryName: string;
  purchaseDate: string;
  cost: string;
  usefulLifeYears: number;
  salvageValue: string;
  status: string;
  monthlyDepreciation: string;
  accumulatedDepreciation: string;
  bookValue: string;
}

export interface PartnerPosition {
  partnerId: number;
  fullName: string;
  capitalContributed: string;
  drawings: string;
  netPosition: string;
  ownershipPercent: string;
}

export interface CategorySpend {
  categoryId: number | null;
  name: string;
  amount: string;
}

export interface FinanceDashboard {
  accounts: Account[];
  totalOnHand: string;
  totalCapital: string;
  totalSpent: string;
  month: string;
  thisMonthExpense: string;
  thisMonthByCategory: CategorySpend[];
  pendingApprovals: number;
  totalPayables: string;
}

export function useAccountLedger(accountId: number | null): UseQueryResult<Ledger> {
  return useQuery({
    queryKey: ['accounts', accountId, 'ledger'],
    queryFn: async () => (await api.get<Envelope<Ledger>>(`/accounts/${String(accountId)}/ledger`)).data.data,
    enabled: accountId !== null,
  });
}

export function useVendors(): UseQueryResult<Vendor[]> {
  return useQuery({
    queryKey: ['vendors'],
    queryFn: async () => (await api.get<Envelope<Vendor[]>>('/vendors')).data.data,
  });
}

export function useAssets(): UseQueryResult<Asset[]> {
  return useQuery({
    queryKey: ['assets'],
    queryFn: async () => (await api.get<Envelope<Asset[]>>('/assets')).data.data,
  });
}

export interface NewAccount {
  name: string;
  type: 'cash' | 'bank' | 'mfs';
  number?: string;
}

export interface AccountChanges {
  name?: string;
  number?: string | null;
  /** BR-08 — retired, never deleted. */
  isActive?: boolean;
}

function useAccountInvalidation(): () => Promise<void> {
  const queryClient = useQueryClient();

  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: financeKeys.accounts() }),
      // Every screen that spends money offers this list.
      queryClient.invalidateQueries({ queryKey: financeKeys.dashboard() }),
    ]);
  };
}

/**
 * Adds a money account (BR-05).
 *
 * There is no opening balance to send. Balances are derived from approved
 * transactions on every read (MONEY-3), so a new account starts at zero by
 * construction — an opening balance is a `capital_in` or a transfer somebody
 * records, not a number typed into a form.
 */
export function useCreateAccount(): UseMutationResult<Account, unknown, NewAccount> {
  const invalidate = useAccountInvalidation();

  return useMutation({
    mutationFn: async (input: NewAccount) => (await api.post<Envelope<Account>>('/accounts', input)).data.data,
    onSuccess: invalidate,
  });
}

export function useUpdateAccount(): UseMutationResult<Account, unknown, { id: number; changes: AccountChanges }> {
  const invalidate = useAccountInvalidation();

  return useMutation({
    mutationFn: async ({ id, changes }) =>
      (await api.patch<Envelope<Account>>(`/accounts/${String(id)}`, changes)).data.data,
    onSuccess: invalidate,
  });
}

export interface AssetCategory {
  id: number;
  name: string;
  usefulLifeYears: number;
  isActive: boolean;
}

/**
 * The kinds of asset the farm records (BR-09, D-018).
 *
 * Reference data, and read far more often than it changes — the assets list
 * labels every row from it.
 */
export function useAssetCategories(): UseQueryResult<AssetCategory[]> {
  return useQuery({
    queryKey: ['assets', 'categories'],
    queryFn: async () => (await api.get<Envelope<AssetCategory[]>>('/assets/categories')).data.data,
    staleTime: 30 * 60 * 1000,
  });
}

export function useCreateAssetCategory(): UseMutationResult<
  AssetCategory,
  unknown,
  { name: string; usefulLifeYears: number }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input) => (await api.post<Envelope<AssetCategory>>('/assets/categories', input)).data.data,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['assets'] });
    },
  });
}

export interface NewAsset {
  name: string;
  categoryId: number;
  purchaseDate: string;
  /** A string all the way to the wire (MONEY-1, MONEY-2). */
  cost: string;
  /** Omitted to take the category's documented default life (BR-09). */
  usefulLifeYears?: number;
  salvageValue?: string;

  /**
   * The account the money came out of.
   *
   * Present means the server also posts an `asset_purchase` for the cost and
   * links it to the asset, atomically. Without it the asset is recorded and
   * no balance moves — which is how a ৳2,00,000 shed used to leave the farm's
   * cash untouched.
   */
  accountId?: number;
  vendorId?: number;
  /** BR-06 — required above the threshold, which every asset exceeds. */
  receiptUrl?: string;
  note?: string;
}

/**
 * Records an asset (BR-09).
 *
 * Note what this does *not* do: it moves no money. An asset row says the farm
 * owns a shed and that the shed wears out; the ৳1,20,000 that bought it is a
 * separate expense entry that goes through maker-checker like everything else.
 * Recording only one of the two is the mistake to watch for, which is why the
 * screen says so rather than leaving it implied.
 */
export function useCreateAsset(): UseMutationResult<Asset, unknown, NewAsset> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: NewAsset) => (await api.post<Envelope<Asset>>('/assets', input)).data.data,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['assets'] }),
        // The dashboard carries a depreciation figure derived from the register.
        queryClient.invalidateQueries({ queryKey: financeKeys.dashboard() }),
      ]);
    },
  });
}

export interface PartnerLedgerLine {
  id: number;
  txnDate: string;
  type: string;
  amount: string;
  /** Signed by what it did to the partner's position: capital up, drawing down. */
  effect: string;
  note: string | null;
  status: string;
  /** BR-08a — the admin changed this entry after approval (D-017). */
  amendReason: string | null;
  amendedAt: string | null;
}

export interface PartnerLedger {
  partner: PartnerPosition;
  lines: PartnerLedgerLine[];
}

/**
 * The individual entries behind a partner's total (P1-BR7).
 *
 * The Partners screen shows sums; this is what they are made of. Without it
 * there is no way to find the one wrong entry among twenty — which is why the
 * totals felt frozen even though the correction paths existed.
 */
export function usePartnerLedger(partnerId: number | null): UseQueryResult<PartnerLedger> {
  return useQuery({
    queryKey: ['partners', partnerId, 'ledger'],
    queryFn: async () => (await api.get<Envelope<PartnerLedger>>(`/partners/${String(partnerId)}/ledger`)).data.data,
    enabled: partnerId !== null,
  });
}

export interface Amendment {
  id: number;
  amount?: string;
  txnDate?: string;
  note?: string;
  categoryId?: number;
  accountId?: number;
  /** A replacement photo id from POST /uploads; a receipt is never removed. */
  receiptUrl?: string;
  /** Mandatory (BR-08a). An amount that changes with no explanation is the
   *  dispute this system exists to settle. */
  reason: string;
}

/**
 * Amends an entry as the admin (BR-08a, D-017, D-020).
 *
 * Read D-017 and D-020 before extending this. The amended values take effect
 * without a second partner approving them, and ownership, balances and the P&L
 * follow from them. The audit trail is what makes that recoverable, and it is
 * written server-side inside the same database transaction.
 */
export function useAmendTransaction(): UseMutationResult<Transaction, unknown, Amendment> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...changes }) =>
      (await api.patch<Envelope<Transaction>>(`/transactions/${String(id)}`, changes)).data.data,
    onSuccess: async () => {
      // Ownership, the partner list, the ledger and every balance derive from
      // this figure. Missing one would leave two screens disagreeing about a
      // number a partner is checking by hand.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['partners'] }),
        queryClient.invalidateQueries({ queryKey: ['transactions'] }),
        queryClient.invalidateQueries({ queryKey: financeKeys.accounts() }),
        queryClient.invalidateQueries({ queryKey: financeKeys.dashboard() }),
      ]);
    },
  });
}

/**
 * Voids an entry (BR-08) — the only "delete" the system has.
 *
 * The row stays, marked voided with the reason, and drops out of every total.
 * A transfer is voided as a pair by the server (P1-BR3).
 */
export function useVoidTransaction(): UseMutationResult<Transaction, unknown, { id: number; reason: string }> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, reason }) =>
      (await api.post<Envelope<Transaction>>(`/transactions/${String(id)}/void`, { reason })).data.data,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['partners'] }),
        queryClient.invalidateQueries({ queryKey: ['transactions'] }),
        queryClient.invalidateQueries({ queryKey: financeKeys.accounts() }),
        queryClient.invalidateQueries({ queryKey: financeKeys.dashboard() }),
      ]);
    },
  });
}

export function usePartners(): UseQueryResult<PartnerPosition[]> {
  return useQuery({
    queryKey: ['partners'],
    queryFn: async () => (await api.get<Envelope<PartnerPosition[]>>('/partners')).data.data,
  });
}

export function useFinanceDashboard(): UseQueryResult<FinanceDashboard> {
  return useQuery({
    queryKey: financeKeys.dashboard(),
    queryFn: async () => (await api.get<Envelope<FinanceDashboard>>('/dashboard/finance')).data.data,
  });
}
